import React, { useState, useCallback } from "react";
import MarkdownTestCasePreview from "./MarkdownTestCasePreview";
import {
  useCreateStagingTestCasesVersionMutation,
  useCreateProdTestCasesVersionMutation,
  usePinStagingTestCasesVersionMutation,
  usePinProdTestCasesVersionMutation,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";

const ITEM_TYPE_OPTIONS = [
  { value: "agent_multistep", label: "Multi-step Agent", color: "bg-blue-100 text-blue-700" },
  { value: "verify", label: "Verify", color: "bg-green-100 text-green-700" },
  { value: "navigate", label: "Navigate", color: "bg-gray-100 text-gray-700" },
  { value: "click", label: "Click", color: "bg-orange-100 text-orange-700" },
  { value: "type", label: "Type", color: "bg-purple-100 text-purple-700" },
  { value: "wait", label: "Wait", color: "bg-yellow-100 text-yellow-700" },
  { value: "text_present", label: "Text Present", color: "bg-teal-100 text-teal-700" },
  { value: "element_exists", label: "Element Exists", color: "bg-cyan-100 text-cyan-700" },
];

const URL_PLACEHOLDER_OPTIONS = [
  { value: "root_url", label: "Homepage" },
  { value: "product_page", label: "Product Page" },
  { value: "category_page", label: "Category Page" },
  { value: "cart_page", label: "Cart Page" },
  { value: "search_page", label: "Search Page" },
  { value: "checkout_page", label: "Checkout Page" },
  { value: "specific_url", label: "Specific URL" },
];

const getItemTypeStyle = (type) => ITEM_TYPE_OPTIONS.find(o => o.value === type)?.color || "bg-gray-100 text-gray-700";
const getItemTypeLabel = (type) => ITEM_TYPE_OPTIONS.find(o => o.value === type)?.label || type;

const StepEditor = ({ item, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => (
  <div className="flex gap-2 items-start group">
    <div className="flex flex-col gap-1 pt-1">
      <button onClick={onMoveUp} disabled={isFirst} className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20" title="Move up">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
      </button>
      <span className="w-5 h-5 rounded bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-mono">{index + 1}</span>
      <button onClick={onMoveDown} disabled={isLast} className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20" title="Move down">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
    </div>
    <div className="flex-1 min-w-0 space-y-1.5 p-2 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex gap-2 items-center">
        <select
          value={item.item_type}
          onChange={(e) => onChange({ ...item, item_type: e.target.value })}
          className="px-2 py-1 rounded text-xs font-medium border border-gray-200 focus:ring-1 focus:ring-violet-300 focus:outline-none bg-white"
        >
          {ITEM_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          type="text"
          value={item.title || ""}
          onChange={(e) => onChange({ ...item, title: e.target.value })}
          placeholder="Step title"
          className="flex-1 px-2 py-1 rounded text-sm border border-gray-200 focus:ring-1 focus:ring-violet-300 focus:outline-none"
        />
      </div>
      <textarea
        value={item.objective || ""}
        onChange={(e) => onChange({ ...item, objective: e.target.value })}
        placeholder="Objective — what to do or verify"
        rows={2}
        className="w-full px-2 py-1.5 rounded text-xs border border-gray-200 focus:ring-1 focus:ring-violet-300 focus:outline-none resize-y leading-relaxed"
      />
    </div>
    <button
      onClick={onRemove}
      className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity mt-1"
      title="Remove step"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const TestCaseCard = ({ tc, index, onChange, onRemove, onDuplicate }) => {
  const [expanded, setExpanded] = useState(false);
  const items = tc.items || [];

  const updateItem = (i, newItem) => {
    const newItems = [...items];
    newItems[i] = newItem;
    onChange({ ...tc, items: newItems });
  };
  const removeItem = (i) => onChange({ ...tc, items: items.filter((_, j) => j !== i) });
  const addItem = (type) => onChange({ ...tc, items: [...items, { item_type: type, title: "", objective: "" }] });
  const moveItem = (i, dir) => {
    const newItems = [...items];
    const j = i + dir;
    [newItems[i], newItems[j]] = [newItems[j], newItems[i]];
    onChange({ ...tc, items: newItems });
  };

  return (
    <div className={`rounded-lg border transition-colors ${tc.activated === false ? "opacity-60 border-gray-200 bg-gray-50/50" : "border-gray-200 bg-white"}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{index + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tc.name || ""}
              onChange={(e) => { e.stopPropagation(); onChange({ ...tc, name: e.target.value }); }}
              onClick={(e) => e.stopPropagation()}
              placeholder="Test case name"
              className="text-sm font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-violet-400 focus:ring-0 focus:outline-none px-0 py-0 w-full"
            />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="text"
              value={tc.category || ""}
              onChange={(e) => { e.stopPropagation(); onChange({ ...tc, category: e.target.value }); }}
              onClick={(e) => e.stopPropagation()}
              placeholder="Category"
              className="text-xs text-gray-500 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-violet-400 focus:ring-0 focus:outline-none px-0 py-0 w-32"
            />
            <select
              value={tc.url_placeholder || "root_url"}
              onChange={(e) => { e.stopPropagation(); onChange({ ...tc, url_placeholder: e.target.value }); }}
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-gray-400 border-0 bg-transparent focus:ring-0 focus:outline-none px-0 py-0 cursor-pointer"
            >
              {URL_PLACEHOLDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              type="text"
              value={tc.id || ""}
              onChange={(e) => { e.stopPropagation(); onChange({ ...tc, id: e.target.value }); }}
              onClick={(e) => e.stopPropagation()}
              placeholder="ID"
              className="text-xs text-gray-400 font-mono bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-violet-400 focus:ring-0 focus:outline-none px-0 py-0 w-40"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-400">{items.length} step{items.length !== 1 ? "s" : ""}</span>
          <label className="flex items-center gap-1 text-xs" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={tc.activated !== false} onChange={(e) => onChange({ ...tc, activated: e.target.checked })} className="rounded border-gray-300 text-violet-600 focus:ring-violet-300 w-3.5 h-3.5" />
            <span className="text-gray-400">On</span>
          </label>
          <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="p-1 text-gray-300 hover:text-violet-600" title="Duplicate">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.5a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" /></svg>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1 text-gray-300 hover:text-red-500" title="Remove test case">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
          </button>
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t space-y-2 pt-3">
          {items.map((item, i) => (
            <StepEditor
              key={i}
              item={item}
              index={i}
              onChange={(newItem) => updateItem(i, newItem)}
              onRemove={() => removeItem(i)}
              onMoveUp={() => moveItem(i, -1)}
              onMoveDown={() => moveItem(i, 1)}
              isFirst={i === 0}
              isLast={i === items.length - 1}
            />
          ))}
          <div className="flex gap-2 mt-2 pl-7">
            <button onClick={() => addItem("agent_multistep")} className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors border border-blue-200">
              + Action
            </button>
            <button onClick={() => addItem("verify")} className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors border border-green-200">
              + Verify
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


const TestCaseEditor = ({ container, environment, templateId, websiteName, onDelete }) => {
  const [editTestCases, setEditTestCases] = useState(null);
  const [editMode, setEditMode] = useState("visual");
  const [rawJsonText, setRawJsonText] = useState("");
  const [markdownSubMode, setMarkdownSubMode] = useState("preview");
  const [showHistory, setShowHistory] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);

  const [createStagingVersion, { isLoading: isCreatingStagingVersion }] = useCreateStagingTestCasesVersionMutation();
  const [createProdVersion, { isLoading: isCreatingProdVersion }] = useCreateProdTestCasesVersionMutation();
  const [pinStagingVersion, { isLoading: isPinningStagingVersion }] = usePinStagingTestCasesVersionMutation();
  const [pinProdVersion, { isLoading: isPinningProdVersion }] = usePinProdTestCasesVersionMutation();

  const isCreating = environment === "staging" ? isCreatingStagingVersion : isCreatingProdVersion;
  const isPinning = environment === "staging" ? isPinningStagingVersion : isPinningProdVersion;

  const currentVersion = container.versions?.find(v => v.version_id === container.current_version_id);
  const currentVersionNumber = currentVersion?.version_number || container.versions?.length || 1;
  // Not memoised on purpose: `container.current_test_cases` is a stable prop reference and
  // the `[]` fallback only applies when the container has no test cases; wrapping it in
  // useMemo would change its identity semantics for the `startEditing` callback below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const testCases = container.current_test_cases || [];
  const isEditing = editTestCases !== null;
  const isBase = !container.website_id;

  const startEditing = useCallback(() => {
    const cloned = JSON.parse(JSON.stringify(testCases));
    setEditTestCases(cloned);
    setRawJsonText(JSON.stringify(cloned, null, 2));
    setEditMode("visual");
  }, [testCases]);

  const hasChanges = isEditing && (
    editMode === "json"
      ? rawJsonText !== JSON.stringify(testCases, null, 2)
      : JSON.stringify(editTestCases) !== JSON.stringify(testCases)
  );

  const handleSave = async () => {
    let toSave;
    if (editMode === "json") {
      try { toSave = JSON.parse(rawJsonText); } catch { alert("Invalid JSON"); return; }
    } else {
      toSave = editTestCases;
    }
    if (!toSave) return;
    const createFn = environment === "staging" ? createStagingVersion : createProdVersion;
    await createFn({ containerId: container.container_id, version: { test_cases: toSave } });
    setEditTestCases(null);
    setRawJsonText("");
  };

  const cancelEditing = () => { setEditTestCases(null); setRawJsonText(""); setEditMode("visual"); };

  const updateTestCase = (i, newTc) => {
    const updated = [...editTestCases];
    updated[i] = newTc;
    setEditTestCases(updated);
    setRawJsonText(JSON.stringify(updated, null, 2));
  };
  const removeTestCase = (i) => {
    const updated = editTestCases.filter((_, j) => j !== i);
    setEditTestCases(updated);
    setRawJsonText(JSON.stringify(updated, null, 2));
  };
  const duplicateTestCase = (i) => {
    const dup = JSON.parse(JSON.stringify(editTestCases[i]));
    dup.id = dup.id + "_copy";
    dup.name = dup.name + " (copy)";
    const updated = [...editTestCases];
    updated.splice(i + 1, 0, dup);
    setEditTestCases(updated);
    setRawJsonText(JSON.stringify(updated, null, 2));
  };
  const addTestCase = () => {
    const newTc = { id: `tc_${Date.now()}`, name: "", category: "", url_placeholder: "root_url", activated: true, items: [{ item_type: "agent_multistep", title: "", objective: "" }, { item_type: "verify", title: "", objective: "" }] };
    const updated = [...editTestCases, newTc];
    setEditTestCases(updated);
    setRawJsonText(JSON.stringify(updated, null, 2));
  };

  const syncFromRaw = () => {
    try {
      setEditTestCases(JSON.parse(rawJsonText));
    } catch {
      alert("Invalid JSON");
    }
  };

  const handlePinVersion = async (versionId) => {
    const pinFn = environment === "staging" ? pinStagingVersion : pinProdVersion;
    await pinFn({ containerId: container.container_id, versionId });
  };

  return (
    <div className={`rounded-xl border-2 transition-colors ${isBase ? "border-violet-200 bg-white" : "border-amber-200 bg-white"}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl ${isBase ? "bg-violet-50" : "bg-amber-50"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${isBase ? "bg-violet-500" : "bg-amber-500"}`} />
          <h4 className="font-semibold text-gray-900 text-sm">{isBase ? "Base Test Cases" : websiteName || container.website_id}</h4>
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${isBase ? "bg-violet-100 text-violet-700" : "bg-amber-100 text-amber-700"}`}>v{currentVersionNumber}</span>
          {!isBase && <span className="text-xs text-gray-400">website-specific</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{testCases.length} test case{testCases.length !== 1 ? "s" : ""}</span>
          {container.versions && container.versions.length > 1 && (
            <button onClick={() => setShowHistory(!showHistory)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${showHistory ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              {container.versions.length} versions
            </button>
          )}
          {!isEditing && (
            <>
              <button onClick={startEditing} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${isBase ? "bg-violet-600 text-white hover:bg-violet-700" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
                Edit
              </button>
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                  title="Delete test cases"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isEditing ? (
          <div className="space-y-3">
            {/* Mode toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button onClick={() => { if (editMode === "json") syncFromRaw(); setEditMode("visual"); }} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${editMode === "visual" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  Visual
                </button>
                <button onClick={() => { setRawJsonText(JSON.stringify(editTestCases, null, 2)); setEditMode("json"); }} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${editMode === "json" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  Raw JSON
                </button>
                <button onClick={() => { if (editMode === "json") syncFromRaw(); setEditMode("markdown"); }} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${editMode === "markdown" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  Markdown
                </button>
              </div>
              <span className="text-xs text-gray-400">{editTestCases.length} test cases</span>
            </div>

            {editMode === "json" ? (
              <textarea
                value={rawJsonText}
                onChange={(e) => setRawJsonText(e.target.value)}
                rows={Math.min(Math.max(rawJsonText.split("\n").length + 2, 12), 40)}
                className="w-full p-4 border-2 border-violet-300 rounded-lg text-sm font-mono leading-relaxed focus:border-violet-500 focus:ring-2 focus:ring-violet-200 focus:outline-none resize-y"
              />
            ) : editMode === "markdown" ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => setMarkdownSubMode("preview")} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${markdownSubMode === "preview" ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    Preview
                  </button>
                  <button onClick={() => setMarkdownSubMode("edit")} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${markdownSubMode === "edit" ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    Edit
                  </button>
                </div>
                {markdownSubMode === "preview" ? (
                  <div className="max-h-[70vh] overflow-y-auto">
                    <MarkdownTestCasePreview testCases={editTestCases} readOnly />
                  </div>
                ) : (
                  <div className="max-h-[70vh] overflow-y-auto">
                    <MarkdownTestCasePreview testCases={editTestCases} onUpdateTestCase={updateTestCase} />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                  {editTestCases.map((tc, i) => (
                    <TestCaseCard
                      key={tc.id || i}
                      tc={tc}
                      index={i}
                      onChange={(newTc) => updateTestCase(i, newTc)}
                      onRemove={() => removeTestCase(i)}
                      onDuplicate={() => duplicateTestCase(i)}
                    />
                  ))}
                </div>
                <button onClick={addTestCase} className="w-full py-2.5 border-2 border-dashed border-violet-300 text-violet-600 rounded-lg text-sm font-medium hover:bg-violet-50 hover:border-violet-400 transition-colors flex items-center justify-center gap-2 mt-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                  Add Test Case
                </button>
              </>
            )}

            {/* Save/Cancel */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-3">
                {hasChanges && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
                {!hasChanges && <span className="text-xs text-gray-400">No changes</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={cancelEditing} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={handleSave} disabled={isCreating || !hasChanges} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  {isCreating ? "Saving..." : `Save as v${currentVersionNumber + 1}`}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Read-only view */
          <div className="space-y-2">
            {testCases.length === 0 ? (
              <div onClick={startEditing} className="py-8 text-center text-gray-400 text-sm border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                No test cases — click to add
              </div>
            ) : (
              testCases.map((tc, i) => (
                <div key={tc.id || i} className={`rounded-lg border px-4 py-2.5 ${tc.activated === false ? "opacity-50 bg-gray-50" : "bg-white"}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900">{tc.name || "Untitled"}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {tc.category && <span className="text-xs text-gray-500">{tc.category}</span>}
                        <span className="text-xs text-gray-400">{(tc.items || []).length} steps</span>
                        {tc.activated === false && <span className="px-1 py-0.5 rounded text-xs bg-gray-200 text-gray-500">off</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {(tc.items || []).map((item, j) => (
                        <span key={j} className={`px-1.5 py-0.5 rounded text-xs font-medium ${getItemTypeStyle(item.item_type)}`}>
                          {getItemTypeLabel(item.item_type).charAt(0)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Version history */}
      {showHistory && container.versions && container.versions.length > 0 && (
        <div className="border-t px-4 py-3 bg-gray-50 rounded-b-xl">
          <div className="space-y-1.5">
            {container.versions.map((version) => {
              const isCurrent = version.version_id === container.current_version_id;
              const isExpanded = expandedVersion === version.version_id;
              return (
                <div key={version.version_id} className={`rounded-lg overflow-hidden ${isCurrent ? "ring-2 ring-violet-300" : "border border-gray-200"}`}>
                  <div className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${isCurrent ? "bg-violet-50 hover:bg-violet-100" : "bg-white hover:bg-gray-50"}`} onClick={() => setExpandedVersion(isExpanded ? null : version.version_id)}>
                    <div className="flex items-center gap-2.5">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isCurrent ? "bg-violet-600 text-white" : "bg-gray-200 text-gray-600"}`}>{version.version_number}</span>
                      {isCurrent && <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-100 text-violet-700">LIVE</span>}
                      <span className="text-xs text-gray-500">{new Date(version.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      {version.created_by && <span className="text-xs text-gray-400">by {version.created_by}</span>}
                      <span className="text-xs text-gray-400">{(version.test_cases || []).length} test cases</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCurrent && (
                        <button onClick={(e) => { e.stopPropagation(); handlePinVersion(version.version_id); }} disabled={isPinning} className="px-2 py-1 text-xs font-medium text-violet-600 hover:text-white hover:bg-violet-600 rounded transition-colors disabled:opacity-50">
                          {isPinning ? "..." : "Restore"}
                        </button>
                      )}
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 bg-white">
                      <pre className="p-3 bg-gray-50 rounded text-xs whitespace-pre-wrap font-mono max-h-48 overflow-y-auto leading-relaxed border">
                        {JSON.stringify(version.test_cases || [], null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCaseEditor;
